import { useEffect, useState, useCallback } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, Heart, ArrowRight } from "lucide-react";

const SLIDES = [
  {
    id: 0,
    image:
      "https://static.prod-images.emergentagent.com/jobs/34a752f1-0d81-4162-98d9-466f130466da/images/241396fa1857bef64b589bbf9b20a0eac09ec6cd9dedd9f9485275207579f472.png",
    overline: "Who We Are",
    title: "Building resilient communities, worldwide.",
    subtitle:
      "TMB Global Foundation expands access to healthcare, energy, food, education and digital infrastructure — designing scalable solutions where they matter most.",
    ctaPrimary: { label: "Our Mission", to: "/mission" },
    ctaSecondary: { label: "Our Vision", to: "/vision" },
  },
  {
    id: 1,
    image:
      "https://static.prod-images.emergentagent.com/jobs/34a752f1-0d81-4162-98d9-466f130466da/images/f02eddfafe86880281dfe4f20e09317e1126a873b882438f425ee735f4fc5ede.png",
    overline: "Health Access & System Innovation",
    title: "Care that reaches every community.",
    subtitle:
      "Technology-enabled, community-based health access focused on prevention, early detection and stronger outcomes for underserved regions.",
    ctaPrimary: { label: "About Us", to: "/about" },
    ctaSecondary: { label: "Our Mission", to: "/mission" },
  },
  {
    id: 2,
    image:
      "https://static.prod-images.emergentagent.com/jobs/34a752f1-0d81-4162-98d9-466f130466da/images/9487f5669daf1b7a51022e9ccebdeab3778f6bf109027b9add948838a00b4953.png",
    overline: "Stand With Us",
    title: "Your support powers global change.",
    subtitle:
      "Every contribution funds solar-powered clinics, clean food systems, future-ready skills and the digital trust infrastructure communities deserve.",
    ctaPrimary: { label: "Donate Now", to: "/donate", icon: true },
    ctaSecondary: { label: "Contact Us", to: "/contact" },
  },
];

export default function HeroCarousel() {
  const [index, setIndex] = useState(0);

  const next = useCallback(() => setIndex((i) => (i + 1) % SLIDES.length), []);
  const prev = useCallback(
    () => setIndex((i) => (i - 1 + SLIDES.length) % SLIDES.length),
    []
  );

  useEffect(() => {
    const t = setInterval(next, 6000);
    return () => clearInterval(t);
  }, [next]);

  const slide = SLIDES[index];

  return (
    <section
      data-testid="hero-carousel"
      className="relative w-full h-[88vh] min-h-[560px] overflow-hidden bg-[#1A2421]"
    >
      <AnimatePresence mode="sync">
        <motion.div
          key={slide.id}
          initial={{ opacity: 0, scale: 1.05 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 1.02 }}
          transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
          className="absolute inset-0"
        >
          <img
            src={slide.image}
            alt={slide.title}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/40 to-black/20" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />
        </motion.div>
      </AnimatePresence>

      <div className="relative z-10 h-full container-tmb px-6 flex items-end md:items-center pb-24 md:pb-0">
        <div className="max-w-2xl text-white">
          <AnimatePresence mode="wait">
            <motion.div
              key={slide.id + "-content"}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.7, delay: 0.2 }}
            >
              <div className="text-xs uppercase tracking-[0.3em] font-bold text-[#D99A29] mb-5">
                {slide.overline}
              </div>
              <h1 className="font-display text-5xl md:text-6xl lg:text-7xl tracking-tight leading-[1.05] mb-6">
                {slide.title}
              </h1>
              <p className="text-base md:text-lg text-white/85 max-w-xl leading-relaxed mb-8">
                {slide.subtitle}
              </p>
              <div className="flex flex-wrap items-center gap-3">
                <Link
                  to={slide.ctaPrimary.to}
                  data-testid={`hero-cta-primary-${slide.id}`}
                  className="btn-primary"
                >
                  {slide.ctaPrimary.icon && <Heart className="w-4 h-4" />}
                  {slide.ctaPrimary.label}
                  <ArrowRight className="w-4 h-4" />
                </Link>
                <Link
                  to={slide.ctaSecondary.to}
                  data-testid={`hero-cta-secondary-${slide.id}`}
                  className="btn-ghost"
                  style={{ borderColor: "rgba(255,255,255,0.4)", color: "white" }}
                >
                  {slide.ctaSecondary.label}
                </Link>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>

      <button
        data-testid="hero-prev"
        onClick={prev}
        aria-label="Previous slide"
        className="hidden md:flex absolute left-6 top-1/2 -translate-y-1/2 z-20 w-12 h-12 rounded-full bg-white/15 hover:bg-white/30 backdrop-blur-md text-white items-center justify-center transition-all"
      >
        <ChevronLeft className="w-5 h-5" />
      </button>
      <button
        data-testid="hero-next"
        onClick={next}
        aria-label="Next slide"
        className="hidden md:flex absolute right-6 top-1/2 -translate-y-1/2 z-20 w-12 h-12 rounded-full bg-white/15 hover:bg-white/30 backdrop-blur-md text-white items-center justify-center transition-all"
      >
        <ChevronRight className="w-5 h-5" />
      </button>

      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 flex items-center gap-3">
        {SLIDES.map((s, i) => (
          <button
            key={s.id}
            data-testid={`hero-dot-${i}`}
            onClick={() => setIndex(i)}
            aria-label={`Go to slide ${i + 1}`}
            className={`h-1.5 rounded-full transition-all ${
              i === index ? "bg-[#C85A3A] w-12" : "bg-white/50 w-6 hover:bg-white/80"
            }`}
          />
        ))}
      </div>
    </section>
  );
}
